import React, { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { NyomiriCard } from './NyomiriCard.tsx';
import { NyomiriSkeletonCard } from '../skeletons/NyomiriSkeletons.tsx';
import { mediaTitle, type HomeMedia } from '../../../metadata/HomeRecommendations.ts';

/**
 * NyomiriRail: Unified horizontal carousel for Continue Watching and Discovery
 * Features:
 * - Clean header with title and 32x32px ghost buttons
 * - Block scroll keeping 1 context card
 * - Snap-to-card, no visible scrollbar
 * - Responsive skeleton states matching exact dimensions (176px, 2:3)
 */

export interface NyomiriRailItem {
  id: string | number;
  title: string;
  coverUrl?: string | null;
  subtitle?: string | null;
  format?: string | null;
  averageScore?: number | null;
  progress?: number | null;
  href?: string;
  media?: HomeMedia;
  onClick: (e?: React.MouseEvent<HTMLElement>) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void;
  onOpenMenu?: (e: React.MouseEvent<HTMLElement>) => void;
}

interface NyomiriRailProps {
  title: string;
  items?: NyomiriRailItem[];
  mediaItems?: HomeMedia[];
  onOpenMedia?: (item: HomeMedia) => void;
  isLoading?: boolean;
}

export function NyomiriRail({
  title,
  items,
  mediaItems,
  onOpenMedia,
  isLoading = false,
}: NyomiriRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const [edges, setEdges] = useState({ left: false, right: false });

  // Map mediaItems to unified rail item if provided
  const normalizedItems: NyomiriRailItem[] = React.useMemo(() => {
    if (items) return items;
    if (mediaItems && onOpenMedia) {
      return mediaItems.map((item) => ({
        id: item.id,
        title: mediaTitle(item),
        coverUrl: item.coverImage?.extraLarge || item.coverImage?.large,
        subtitle: [item.startDate?.year, item.genres?.[0]].filter(Boolean).join(' · '),
        format: item.format,
        averageScore: item.averageScore,
        href: `/?section=detail&media=${item.id}`,
        media: item,
        onClick: (e?: React.MouseEvent<HTMLElement>) => {
          e?.preventDefault();
          onOpenMedia(item);
        },
      }));
    }
    return [];
  }, [items, mediaItems, onOpenMedia]);

  const hasContent = normalizedItems.length > 0;

  // Track scroll edges to enable/disable arrow buttons
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    const update = () => {
      const atLeft = el.scrollLeft > 4;
      const atRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 6;
      setEdges({ left: atLeft, right: atRight });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', update);
    };
  }, [normalizedItems.length]);

  const handleScroll = (direction: -1 | 1) => {
    const el = railRef.current;
    if (!el) return;

    // Scroll by block in exact multiples of 192px (176px + 16px gap)
    const distance = Math.max(1, Math.floor((el.clientWidth - 32) / 192)) * 192;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    el.scrollBy({
      left: direction * distance,
      behavior: reduceMotion ? 'instant' : 'smooth',
    });
  };

  if (!isLoading && !hasContent) return null;

  return (
    <Box
      component="section"
      className="nyomiri-rail"
      aria-label={title}
      sx={{
        minWidth: 0,
        position: 'relative',
        '&:hover .nyomiri-rail-edge-btn': {
          opacity: 1,
        },
      }}
    >
      {/* Header with Title & Arrow Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1.8,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: 'var(--font-display, "Bebas Neue", sans-serif)',
            fontSize: { xs: 22, sm: 25 },
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.1,
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: 3.5,
              height: 18,
              borderRadius: 1,
              bgcolor: 'var(--accent-500, #ff2e63)',
              boxShadow: '0 0 8px var(--accent-500, #ff2e63)',
            },
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ButtonBase
            type="button"
            onClick={() => handleScroll(-1)}
            disabled={!edges.left}
            aria-label={`Desplazar hacia la izquierda en ${title}`}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              bgcolor: 'rgba(16, 16, 24, 0.75)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.18s ease',
              '&:hover:not(.Mui-disabled)': {
                bgcolor: 'rgba(30, 30, 44, 0.95)',
                borderColor: 'var(--accent-500, #ff2e63)',
                color: 'var(--text-primary)',
              },
              '&.Mui-disabled': {
                opacity: 0.25,
                cursor: 'default',
              },
            }}
          >
            <ChevronLeft sx={{ fontSize: 20 }} />
          </ButtonBase>

          <ButtonBase
            type="button"
            onClick={() => handleScroll(1)}
            disabled={!edges.right}
            aria-label={`Desplazar hacia la derecha en ${title}`}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              bgcolor: 'rgba(16, 16, 24, 0.75)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.18s ease',
              '&:hover:not(.Mui-disabled)': {
                bgcolor: 'rgba(30, 30, 44, 0.95)',
                borderColor: 'var(--accent-500, #ff2e63)',
                color: 'var(--text-primary)',
              },
              '&.Mui-disabled': {
                opacity: 0.25,
                cursor: 'default',
              },
            }}
          >
            <ChevronRight sx={{ fontSize: 20 }} />
          </ButtonBase>
        </Box>
      </Box>

      {/* Cards Viewport Wrapper with Immersive Netflix Edge Paddles */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Left Floating Edge Paddle */}
        {edges.left && (
          <ButtonBase
            className="nyomiri-rail-edge-btn"
            onClick={() => handleScroll(-1)}
            aria-label={`Desplazar izquierda en ${title}`}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: -12,
              width: 48,
              zIndex: 12,
              opacity: 0,
              transition: 'opacity 0.2s ease, background 0.2s ease',
              background: 'linear-gradient(to right, rgba(5, 5, 8, 0.95) 0%, rgba(5, 5, 8, 0.5) 60%, transparent 100%)',
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              '&:hover': {
                color: 'var(--accent-500, #ff2e63)',
              },
            }}
          >
            <ChevronLeft sx={{ fontSize: 32 }} />
          </ButtonBase>
        )}

        {/* Right Floating Edge Paddle */}
        {edges.right && (
          <ButtonBase
            className="nyomiri-rail-edge-btn"
            onClick={() => handleScroll(1)}
            aria-label={`Desplazar derecha en ${title}`}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: -12,
              width: 48,
              zIndex: 12,
              opacity: 0,
              transition: 'opacity 0.2s ease, background 0.2s ease',
              background: 'linear-gradient(to left, rgba(5, 5, 8, 0.95) 0%, rgba(5, 5, 8, 0.5) 60%, transparent 100%)',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              '&:hover': {
                color: 'var(--accent-500, #ff2e63)',
              },
            }}
          >
            <ChevronRight sx={{ fontSize: 32 }} />
          </ButtonBase>
        )}

        {/* Cards Viewport */}
        <Box
          ref={railRef}
          id={id}
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2.25 },
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollPaddingInlineStart: 2,
            scrollbarWidth: 'none',
            py: 1.5,
            px: 0.5,
            margin: '-12px -4px',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {isLoading && !hasContent
            ? Array.from({ length: 7 }).map((_, i) => (
                <NyomiriSkeletonCard key={i} />
              ))
            : normalizedItems.map((item) => {
                return (
                  <NyomiriCard
                    key={item.id}
                    title={item.title}
                    coverUrl={item.coverUrl}
                    subtitle={item.subtitle}
                    format={item.format}
                    averageScore={item.averageScore}
                    progress={item.progress}
                    href={item.href}
                    onClick={item.onClick}
                    onContextMenu={item.onContextMenu}
                    onOpenMenu={item.onOpenMenu}
                  />
                );
              })}
        </Box>
      </Box>
    </Box>
  );
}
